import React, { useCallback } from 'react';
import { X, ArrowUpRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { markPopupDismissed } from '../lib/popupManager';
import { usePopupVisibility } from './apparel-promo/usePopupVisibility';

const POPUP_ID = 'peptiq_promo';
const CLAIMED_KEY = 'peptiq_promo_claimed';
const PEPTIQ_URL = 'https://peptiq.io/go/ninjathletes?utm_source=affiliate&utm_medium=partner&utm_campaign=NINJA&dub_id=aFFKDRhZrnOb5qiU';

/**
 * Contextual affiliate popup promoting the PeptiQ tracking app. Surfaces only
 * on peptide-related pages (PeptidesHubPage + ArticlePage when category === 'Peptides').
 * Triggers on 8s delay, 35% scroll, or exit-intent — whichever fires first.
 */
export default function PeptiQPopup() {
  const [show, setShow] = usePopupVisibility(POPUP_ID, {
    delayMs: 8000,
    scrollThreshold: 0.35,
    exitIntent: true,
    claimedKey: CLAIMED_KEY,
  });

  const dismiss = useCallback(() => {
    setShow(false);
    markPopupDismissed(POPUP_ID);
  }, [setShow]);

  const claimAndOpen = useCallback(() => {
    // localStorage usage: non-sensitive one-time "claimed" flag so the popup
    // doesn't re-appear after the user has clicked through
    localStorage.setItem(CLAIMED_KEY, 'true');
    window.open(PEPTIQ_URL, '_blank', 'noopener,noreferrer');
    setShow(false);
  }, [setShow]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4" data-testid="peptiq-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#00F0FF]/30 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-300 shadow-[0_0_60px_-12px_rgba(0,240,255,0.35)]">
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-2.5 right-2.5 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-900 border border-zinc-700 text-white hover:bg-zinc-700 hover:border-zinc-500 transition-all"
          data-testid="peptiq-popup-close"
          aria-label="Close popup"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header strip */}
        <div className="relative bg-gradient-to-r from-[#00F0FF]/10 via-[#00F0FF]/5 to-transparent px-6 pt-6 pb-3 border-b border-[#00F0FF]/10">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" strokeWidth={2.5} />
            <span
              className="text-[#00F0FF] text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Partner App · Free
            </span>
          </div>
          <h3
            className="text-white text-2xl leading-tight"
            style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}
            data-testid="peptiq-popup-headline"
          >
            Track your peptide protocol with PeptiQ
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-zinc-400 text-sm leading-relaxed mb-4">
            Free app made by athletes, for athletes. Log doses, track cycles, and stack with confidence.
          </p>

          <ul className="space-y-2 mb-5">
            {[
              'Auto-calc reconstitution + dosing',
              'Cycle reminders & lab-marker logs',
              'Stack templates from this site',
            ].map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-zinc-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={claimAndOpen}
            className="w-full py-3.5 bg-[#00F0FF] text-black font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-white transition-colors group"
            style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.05em' }}
            data-testid="peptiq-popup-cta"
          >
            Get the app
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>

          <button
            onClick={dismiss}
            className="w-full text-center text-zinc-500 text-xs mt-3 hover:text-zinc-300 transition-colors py-1"
            data-testid="peptiq-popup-skip"
          >
            No thanks
          </button>

          <p className="text-zinc-700 text-[10px] text-center mt-2">
            Opens in a new tab · We may earn a small commission at no cost to you
          </p>
        </div>
      </div>
    </div>
  );
}
